import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { uploadFile } from 'api'
import {
  fetchAdminWeeklyBundle,
  fetchPreschoolCentres,
  saveAdminEvaluationPhoto
} from 'api/preschoolEvaluation'
import { uploadPresets } from 'app/config'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

import {
  NUMERIC_FIXED_GOAL,
  NUMERIC_FIXED_GOAL_ID,
  PRESCHOOL_LEVEL_OPTIONS,
  SEQUENTIAL_FIXED_GOALS,
  sequentialGoalId,
  sequentialStrandLabel
} from '../constants'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  spacer: { flexGrow: 1 },
  weekInfo: { color: theme.palette.text.secondary },
  card: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: theme.spacing(1)
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  studentRow: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1)
  },
  studentRowStatic: { cursor: 'default' },
  detail: { padding: theme.spacing(0, 2, 2, 2), marginTop: theme.spacing(-1) },
  skillName: {
    fontWeight: 700,
    fontSize: 15,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(1)
  },
  goalText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
  },
  helpText: { color: theme.palette.text.secondary, fontSize: 12 },
  observation: {
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1),
    whiteSpace: 'pre-wrap'
  },
  muted: { color: theme.palette.text.secondary, fontStyle: 'italic' },
  goalBlock: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  teacherName: { color: theme.palette.text.secondary, fontSize: 12 },
  photoStrip: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  photoThumb: { position: 'relative', width: 64, height: 64 },
  photoImg: {
    width: 64,
    height: 64,
    objectFit: 'cover',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    display: 'block'
  },
  photoRemove: {
    position: 'absolute',
    top: -8,
    right: -8,
    padding: 2,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': { background: theme.palette.background.paper }
  }
}))

// Monday of the week containing `dateStr`, plus the month key. Copied from the
// teacher WeeklyEvaluation page so the week snapping matches exactly.
const computeWeek = (dateStr) => {
  const d = dayjs(dateStr)
  if (!d.isValid()) return { weekStart: '', monthKey: '' }
  const dow = d.day()
  const diff = dow === 0 ? -6 : 1 - dow
  const monday = d.add(diff, 'day')
  return {
    weekStart: monday.format('YYYY-MM-DD'),
    monthKey: monday.format('YYYY-MM')
  }
}

// Shape one student's saved evaluation from the bundle. Identical logic to the
// teacher page's seedForm, so what the admin reviews matches the teacher form.
const seedForm = (bundle, studentUuid) => {
  const existing = bundle?.existing_evaluations?.[studentUuid] || null
  const plan = bundle?.plan || null
  const skillsDict = bundle?.skills || {}
  const sequentialSkills = Object.values(skillsDict).filter(
    (s) => s && s.type === 'sequential'
  )
  const skillNameById = (id) => (skillsDict[id] && skillsDict[id].name) || ''

  let spiral
  if (existing && existing.spiral && existing.spiral.length) {
    spiral = existing.spiral.map((item) => ({
      ...item,
      skill_name: item.skill_name || skillNameById(item.skill_id)
    }))
  } else {
    spiral = []
    ;(plan?.spiral || []).forEach((sk) => {
      ;(sk.goals || []).forEach((g) => {
        spiral.push({
          skill_id: sk.skill_id,
          skill_name: skillNameById(sk.skill_id),
          goal_id: g.goal_id,
          goal_text: g.text,
          value: ''
        })
      })
    })
  }

  const isLkg = bundle?.level === 'LKG'
  let sequential
  if (existing && existing.sequential && existing.sequential.length) {
    sequential = existing.sequential.map((sk) => ({
      ...sk,
      skill_name: sk.skill_name || skillNameById(sk.skill_id)
    }))
  } else if (isLkg) {
    sequential = (plan?.sequential || []).map((sp) => ({
      skill_id: sp.skill_id,
      skill_name: skillNameById(sp.skill_id),
      goals: (sp.goals || []).map((g) => ({
        goal_id: g.goal_id,
        goal_text: g.text,
        value: ''
      }))
    }))
  } else {
    sequential = sequentialSkills.map((s) => ({
      skill_id: s.uuid,
      skill_name: s.name,
      focus_text: plan?.sequential_focus_by_skill_id?.[s.uuid] || '',
      goals: SEQUENTIAL_FIXED_GOALS.map((g, i) => ({
        goal_id: sequentialGoalId(i),
        goal_text: g,
        evaluation: ''
      }))
    }))
  }

  const exNum = (existing && existing.numeric) || {}
  const numeric = {
    focus: exNum.focus != null ? exNum.focus : plan?.numeric_focus ?? '',
    goal_id: NUMERIC_FIXED_GOAL_ID,
    goal_text: exNum.goal_text || NUMERIC_FIXED_GOAL,
    evaluation: exNum.evaluation || '',
    comment: exNum.comment || ''
  }

  return { spiral, sequential, numeric }
}

const Observation = ({ value }) => {
  const classes = useStyles()
  if (value == null || `${value}`.trim() === '') {
    return (
      <Typography variant="body2" className={classes.muted}>
        Not recorded
      </Typography>
    )
  }
  return (
    <Typography variant="body2" className={classes.observation}>
      {value}
    </Typography>
  )
}

// Photos on one observation — thumbnails (click to open) + add/remove. This is
// the only editable part of the admin view: ratings/text stay read-only, photos
// don't (the reason this page needed a write path at all).
const PhotoStrip = ({ goalId, imageUrls, onAdd, onRemove }) => {
  const classes = useStyles()
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const imgs = imageUrls || []

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = '' // allow re-picking the same file
    if (!file) return
    setBusy(true)
    try {
      await onAdd(goalId, file)
    } catch (err) {
      showToast('Photo upload failed', 'danger')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (url) => {
    setBusy(true)
    try {
      await onRemove(goalId, url)
    } catch (err) {
      showToast('Could not remove the photo', 'danger')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box className={classes.photoStrip}>
      {imgs.map((url) => (
        <Box key={url} className={classes.photoThumb}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt="observation" className={classes.photoImg} />
          </a>
          <IconButton
            size="small"
            className={classes.photoRemove}
            onClick={() => remove(url)}
            disabled={busy}
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        variant="outlined"
        onClick={() => inputRef.current && inputRef.current.click()}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={14} /> : null}
      >
        {busy ? 'Uploading…' : '+ Photo'}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onFile}
      />
    </Box>
  )
}

// Render of one student's submitted evaluation. Ratings/text read-only; each
// observation carries a PhotoStrip so an admin can attach/remove photos.
const StudentDetail = ({
  bundle,
  studentUuid,
  level,
  onAddPhoto,
  onRemovePhoto
}) => {
  const classes = useStyles()
  const { spiral, sequential, numeric } = seedForm(bundle, studentUuid)
  // Number Sense is only for Nursery onward — hidden for Toddler / Pre-Nursery.
  const hasNumericGoal =
    !['TODLR', 'PRENUR'].includes(level) &&
    bundle?.plan?.numeric_focus != null &&
    `${bundle.plan.numeric_focus}`.trim() !== ''

  return (
    <Box className={classes.detail}>
      {spiral.length ? (
        <>
          <Typography className={classes.sectionTitle}>Observations</Typography>
          {spiral.map((item, i) => (
            <Box key={`${item.goal_id}-${i}`} className={classes.goalBlock}>
              {item.skill_name &&
                (i === 0 || spiral[i - 1].skill_id !== item.skill_id) && (
                  <Typography variant="subtitle1" className={classes.skillName}>
                    {item.skill_name}
                  </Typography>
                )}
              <Typography variant="body2" className={classes.goalText}>
                {item.goal_text}
              </Typography>
              <Observation value={item.value} />
              <PhotoStrip
                goalId={item.goal_id}
                imageUrls={item.image_urls}
                onAdd={onAddPhoto}
                onRemove={onRemovePhoto}
              />
            </Box>
          ))}
        </>
      ) : null}

      {sequential.length ? (
        <>
          <Typography
            className={classes.sectionTitle}
            style={{ marginTop: 12 }}
          >
            {sequentialStrandLabel(level)}
          </Typography>
          {sequential.map((sk) => (
            <Box key={sk.skill_id} className={classes.goalBlock}>
              {sequential.length > 1 && (
                <Typography variant="subtitle1" className={classes.skillName}>
                  {sk.skill_name}
                </Typography>
              )}
              {level === 'LKG' ? (
                (sk.goals || []).map((g) => (
                  <Box key={g.goal_id} style={{ marginTop: 8 }}>
                    <Typography variant="body2" className={classes.goalText}>
                      {g.goal_text}
                    </Typography>
                    <Observation value={g.value} />
                    <PhotoStrip
                      goalId={g.goal_id}
                      imageUrls={g.image_urls}
                      onAdd={onAddPhoto}
                      onRemove={onRemovePhoto}
                    />
                  </Box>
                ))
              ) : (
                <>
                  {sk.focus_text ? (
                    <Typography variant="body2" className={classes.goalText}>
                      Focus: {sk.focus_text}
                    </Typography>
                  ) : null}
                  {(sk.goals || []).map((g) => (
                    <Box key={g.goal_id} style={{ marginTop: 6 }}>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12
                        }}
                      >
                        <Typography
                          variant="body2"
                          className={classes.goalText}
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          {g.goal_text}
                        </Typography>
                        <Typography variant="body2">
                          {g.evaluation || '—'}
                        </Typography>
                      </Box>
                      <PhotoStrip
                        goalId={g.goal_id}
                        imageUrls={g.image_urls}
                        onAdd={onAddPhoto}
                        onRemove={onRemovePhoto}
                      />
                    </Box>
                  ))}
                </>
              )}
            </Box>
          ))}
        </>
      ) : null}

      {hasNumericGoal ? (
        <>
          <Typography
            className={classes.sectionTitle}
            style={{ marginTop: 12 }}
          >
            Number sense
          </Typography>
          <Typography variant="body2" className={classes.helpText}>
            Focus number:{' '}
            {numeric.focus === '' || numeric.focus == null
              ? '—'
              : numeric.focus}
          </Typography>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 6
            }}
          >
            <Typography variant="body2" style={{ flex: 1 }}>
              {numeric.goal_text || NUMERIC_FIXED_GOAL}
            </Typography>
            <Typography variant="body2">{numeric.evaluation || '—'}</Typography>
          </Box>
          {numeric.comment ? (
            <Typography variant="body2" style={{ marginTop: 6 }}>
              <strong>Comment:</strong> {numeric.comment}
            </Typography>
          ) : null}
        </>
      ) : null}
    </Box>
  )
}

const statusChip = (existing) => {
  if (existing?.submitted_at) {
    return (
      <Chip
        size="small"
        label="submitted"
        style={{ backgroundColor: '#d8f3dc', color: '#1B7A3D' }}
      />
    )
  }
  if (existing) {
    return (
      <Chip
        size="small"
        label="draft saved"
        style={{ backgroundColor: '#fdf0d5', color: '#8a6d1a' }}
      />
    )
  }
  return <Chip size="small" label="not started" variant="outlined" />
}

// Admin / super-admin view of what teachers submitted for a cohort. Ratings and
// text are read-only; admins may only add/remove PHOTOS on each observation
// (which flow to the parent + term report via the submission snapshot).
const WeeklyEvaluationView = () => {
  const classes = useStyles()

  const [centres, setCentres] = useState([])
  const [centre, setCentre] = useState('')
  const [level, setLevel] = useState('NUR')
  const [dateValue, setDateValue] = useState(dayjs().format('YYYY-MM-DD'))

  const [bundle, setBundle] = useState(null)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState('')

  const { weekStart, monthKey } = useMemo(
    () => computeWeek(dateValue),
    [dateValue]
  )

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const data = await fetchPreschoolCentres()
          const list = data?.results || (Array.isArray(data) ? data : [])
          if (cancelled) return
          if (list.length) {
            setCentres(list)
            return
          }
        } catch (e) {
          if (cancelled) return
        }
        await new Promise((r) => setTimeout(r, 300 * (attempt + 1)))
      }
      if (!cancelled)
        showToast('Failed to load centres — please reload', 'danger')
    }
    run()
    return () => {
      cancelled = true
    }
  }, [])

  const loadBundle = useCallback(async () => {
    if (!centre || !level || !weekStart) {
      setBundle(null)
      return
    }
    setLoading(true)
    setExpanded('')
    try {
      const data = await fetchAdminWeeklyBundle({
        centre,
        level,
        week_start_date: weekStart
      })
      setBundle(data || null)
    } catch (e) {
      setBundle(null)
      showToast(
        e?.response?.status === 403
          ? 'You do not have access to view this centre.'
          : "Failed to load this week's submissions",
        'danger'
      )
    } finally {
      setLoading(false)
    }
  }, [centre, level, weekStart])

  useEffect(() => {
    loadBundle()
  }, [loadBundle])

  // ---- Photos: the only write path on this page (ratings stay read-only) ----
  // Update the loaded bundle in place so the new/removed photo shows immediately
  // without a full reload. seedForm passes image_urls straight through.
  const applyPhoto = useCallback((studentUuid, goalId, action, url) => {
    setBundle((prev) => {
      const ev = prev?.existing_evaluations?.[studentUuid]
      if (!ev) return prev
      const mut = (imgs) =>
        action === 'add'
          ? (imgs || []).includes(url)
            ? imgs || []
            : [...(imgs || []), url]
          : (imgs || []).filter((u) => u !== url)
      const spiral = (ev.spiral || []).map((it) =>
        it.goal_id === goalId ? { ...it, image_urls: mut(it.image_urls) } : it
      )
      const sequential = (ev.sequential || []).map((sk) => ({
        ...sk,
        goals: (sk.goals || []).map((g) =>
          g.goal_id === goalId ? { ...g, image_urls: mut(g.image_urls) } : g
        )
      }))
      return {
        ...prev,
        existing_evaluations: {
          ...prev.existing_evaluations,
          [studentUuid]: { ...ev, spiral, sequential }
        }
      }
    })
  }, [])

  const addPhoto = useCallback(
    async (studentUuid, goalId, file) => {
      const uploaded = await uploadFile(file, {
        uploadPreset: uploadPresets[constants.OPENBOOK],
        public_id: `weekly_eval_${studentUuid}_${goalId}`
      })
      const url = uploaded?.secure_url || uploaded?.url
      if (!url) throw new Error('upload returned no url')
      await saveAdminEvaluationPhoto({
        student: studentUuid,
        week_start_date: weekStart,
        goal_id: goalId,
        action: 'add',
        url
      })
      applyPhoto(studentUuid, goalId, 'add', url)
    },
    [weekStart, applyPhoto]
  )

  const removePhoto = useCallback(
    async (studentUuid, goalId, url) => {
      await saveAdminEvaluationPhoto({
        student: studentUuid,
        week_start_date: weekStart,
        goal_id: goalId,
        action: 'remove',
        url
      })
      applyPhoto(studentUuid, goalId, 'remove', url)
    },
    [weekStart, applyPhoto]
  )

  const roster = bundle?.roster || []
  const theme = bundle?.theme || null
  const existingByStudent = bundle?.existing_evaluations || {}
  const submittedCount = roster.filter(
    (s) => existingByStudent[s.student_uuid]?.submitted_at
  ).length
  const notStartedCount = roster.filter(
    (s) => !existingByStudent[s.student_uuid]
  ).length

  return (
    <Box className={classes.root}>
      <Box className={classes.topBar}>
        <Chip
          size="small"
          label="Ratings view-only · photos editable"
          style={{ backgroundColor: '#e6f1fb', color: '#185FA5' }}
        />

        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Centre</InputLabel>
          <Select
            label="Centre"
            value={centre}
            onChange={(e) => setCentre(e.target.value)}
          >
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>Level</InputLabel>
          <Select
            label="Level"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          >
            {Object.entries(PRESCHOOL_LEVEL_OPTIONS).map(([code, name]) => (
              <MenuItem key={code} value={code}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Pick any date in the week"
          type="date"
          size="small"
          variant="outlined"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Typography variant="body2" className={classes.weekInfo}>
          Week of <strong>{weekStart || '—'}</strong> · {monthKey}
        </Typography>

        <Box className={classes.spacer} />
        {loading ? <CircularProgress size={22} /> : null}
      </Box>

      {theme ? (
        <Paper variant="outlined" className={classes.card}>
          <Typography className={classes.sectionTitle}>Weekly theme</Typography>
          <Typography variant="body2">
            {theme.subtheme_title || '—'}
            {theme.subtheme_topics ? ` — ${theme.subtheme_topics}` : ''}
          </Typography>
        </Paper>
      ) : null}

      {bundle ? (
        <>
          <Box className={classes.summary}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              Students ({roster.length})
            </Typography>
            <span style={{ color: '#1B7A3D' }}>{submittedCount} submitted</span>
            <span>{notStartedCount} not started</span>
          </Box>

          {roster.map((s) => {
            const existing = existingByStudent[s.student_uuid]
            const canExpand = !!existing
            const isOpen = expanded === s.student_uuid
            return (
              <Paper
                key={s.student_uuid}
                variant="outlined"
                style={{ marginBottom: 8 }}
              >
                <Box
                  className={`${classes.studentRow} ${
                    canExpand ? '' : classes.studentRowStatic
                  }`}
                  onClick={() =>
                    canExpand && setExpanded(isOpen ? '' : s.student_uuid)
                  }
                >
                  <Typography variant="body1">
                    {s.first_name} {s.last_name}
                  </Typography>
                  <Box
                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    {existing?.teacher_name ? (
                      <span className={classes.teacherName}>
                        {existing.teacher_name}
                      </span>
                    ) : null}
                    {statusChip(existing)}
                  </Box>
                </Box>
                {isOpen ? (
                  <StudentDetail
                    bundle={bundle}
                    studentUuid={s.student_uuid}
                    level={level}
                    onAddPhoto={(goalId, file) =>
                      addPhoto(s.student_uuid, goalId, file)
                    }
                    onRemovePhoto={(goalId, url) =>
                      removePhoto(s.student_uuid, goalId, url)
                    }
                  />
                ) : null}
              </Paper>
            )
          })}
        </>
      ) : (
        <Typography className={classes.helpText}>
          Pick a centre, level and week to view submissions.
        </Typography>
      )}
    </Box>
  )
}

export default WeeklyEvaluationView
